import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreditCard, Check } from "lucide-react";

const TIER_ORDER = ["trial", "pro", "enterprise"];
const TIER_STYLE = {
  trial: { grad: "from-emerald-brand/15 to-emerald-brand/5", icon: "bg-emerald-brand/12 text-emerald-brand" },
  pro: { grad: "from-sun/25 to-sun/5", icon: "bg-sun/25 text-yellow-700" },
  enterprise: { grad: "from-crimson/12 to-crimson/5", icon: "bg-crimson/12 text-crimson" },
};

function fmt(v) { return v === 0 ? "Unlimited" : v.toLocaleString(); }

export default function Billing() {
  const { user, activeCompanyId, setActiveCompanyId } = useAuth();
  const [plans, setPlans] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [usage, setUsage] = useState(null);
  const [companyId, setCompanyId] = useState(activeCompanyId || "");

  useEffect(() => {
    (async () => {
      const [p, c] = await Promise.all([api.get("/billing/plans"), api.get("/companies")]);
      setPlans(p.data); setCompanies(c.data);
      const cid = activeCompanyId || c.data[0]?.id || "";
      setCompanyId(cid);
    })();
  }, [activeCompanyId]);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const { data } = await api.get(`/billing/usage/${companyId}`);
        setUsage(data);
      } catch { setUsage(null); }
    })();
  }, [companyId]);

  const setPlan = async (tier) => {
    if (!companyId) return toast.error("Select a company first");
    try {
      await api.post(`/billing/plan/${companyId}`, { tier });
      toast.success(`Plan set to ${tier}`);
      const { data } = await api.get(`/billing/usage/${companyId}`);
      setUsage(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto" data-testid="billing-page">
      <PageHeader
        title="Plans & usage"
        subtitle="Set the plan for each company workspace. Admin-controlled — no payment processing required."
        eyebrow="Billing"
        actions={
          <Select value={companyId} onValueChange={(v) => { setCompanyId(v); setActiveCompanyId(v); }}>
            <SelectTrigger className="rounded-xl w-64" data-testid="billing-company-select"><SelectValue placeholder="Company" /></SelectTrigger>
            <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.trading_name || c.legal_name}</SelectItem>)}</SelectContent>
          </Select>
        }
      />

      <div className="p-6 lg:p-10 space-y-6">
        {usage && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <UsageCard label="Mailboxes" used={usage.mailboxes.used} limit={usage.mailboxes.limit} tone="emerald" />
            <UsageCard label="Emails this month" used={usage.emails.used} limit={usage.emails.limit} tone="sun" />
            <UsageCard label="AI calls this month" used={usage.ai_calls.used} limit={usage.ai_calls.limit} tone="crimson" />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {plans
            .sort((a, b) => TIER_ORDER.indexOf(a.id) - TIER_ORDER.indexOf(b.id))
            .map((p, i) => {
              const style = TIER_STYLE[p.id];
              const isCurrent = usage?.tier === p.id;
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className={`card-3d p-6 relative overflow-hidden bg-gradient-to-br ${style.grad}`}
                  data-testid={`plan-${p.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className={`h-10 w-10 rounded-xl grid place-items-center ${style.icon}`}>
                      <CreditCard className="h-5 w-5" />
                    </div>
                    {isCurrent && (
                      <span className="pill bg-emerald-brand text-white shadow-md shadow-primary/30">
                        <Check className="h-3 w-3 inline mr-1 -mt-0.5" /> Current
                      </span>
                    )}
                  </div>
                  <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{p.label}</div>
                  <div className="mt-1 font-display text-4xl font-semibold tracking-tight">
                    ${p.plan_price_usd}<span className="text-base font-normal text-muted-foreground">/mo</span>
                  </div>
                  <ul className="mt-5 space-y-2 text-sm">
                    <PlanRow>{fmt(p.mailbox_limit)} mailboxes</PlanRow>
                    <PlanRow>{fmt(p.monthly_email_limit)} emails / month</PlanRow>
                    <PlanRow>{fmt(p.monthly_ai_limit)} AI calls / month</PlanRow>
                  </ul>
                  <Button
                    className={`mt-6 w-full rounded-xl h-10 ${isCurrent ? "" : "shadow-lg shadow-primary/20"}`}
                    variant={isCurrent ? "outline" : "default"}
                    disabled={isCurrent || !companyId || (user?.role !== "super_admin" && user?.role !== "company_admin")}
                    onClick={() => setPlan(p.id)}
                    data-testid={`select-plan-${p.id}`}
                  >
                    {isCurrent ? "Active plan" : `Switch to ${p.label}`}
                  </Button>
                </motion.div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

function PlanRow({ children }) {
  return (
    <li className="flex items-start gap-2">
      <Check className="h-4 w-4 text-emerald-brand mt-0.5 shrink-0" />
      <span>{children}</span>
    </li>
  );
}

function UsageCard({ label, used, limit, tone }) {
  const pct = limit === 0 ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const toneMap = { emerald: "from-emerald-brand to-emerald-brand", sun: "from-sun to-sun", crimson: "from-crimson to-crimson" };
  return (
    <div className="card-3d p-5">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-2 font-display text-3xl font-semibold tracking-tight">
        {used.toLocaleString()}
        <span className="text-base font-normal text-muted-foreground ml-1">/ {limit === 0 ? "∞" : limit.toLocaleString()}</span>
      </div>
      <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6 }}
          className={`h-full bg-gradient-to-r ${toneMap[tone]}`}
        />
      </div>
      <div className="mt-2 text-xs font-mono text-muted-foreground">
        {limit === 0 ? "Unlimited" : `${pct}% used`}
      </div>
    </div>
  );
}
